'use client'

import Navigation from '@/components/velocity/Navigation'
import Hero from '@/components/velocity/Hero'
import Features from '@/components/velocity/Features'
import RateCalculator from '@/components/velocity/RateCalculator'
import DashboardPreview from '@/components/velocity/DashboardPreview'
import HospitalPortal from '@/components/velocity/HospitalPortal'
import Marketplace from '@/components/velocity/Marketplace'
import AIChatbot from '@/components/velocity/AIChatbot'
import { motion } from 'framer-motion'
import { Sparkles, Users, Building2, Heart } from 'lucide-react'

export default function Home() {
  return (
    <main className="relative bg-gray-900 overflow-x-hidden">
      <Navigation />
      <Hero />
      <Features />
      <RateCalculator />
      <DashboardPreview />
      <Marketplace />
      <HospitalPortal />

      {/* Social Proof Section */}
      <section className="py-20 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Join the <span className="text-gradient bg-gradient-to-r from-accent-400 to-primary-400">Movement</span>
            </h2>
            <p className="text-xl text-gray-300">
              Thousands of nurses and hospitals trust Velocity
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Users, value: '12,340+', label: 'Active Nurses' },
              { icon: Building2, value: '850+', label: 'Partner Hospitals' },
              { icon: Sparkles, value: '45,000+', label: 'Successful Placements' },
              { icon: Heart, value: '98%', label: 'Satisfaction Rate' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-effect rounded-3xl p-8 text-center hover:bg-white/10 transition-all"
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex p-4 bg-gradient-to-r from-accent-500 to-primary-500 rounded-2xl mb-4">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold text-gradient bg-gradient-to-r from-accent-400 to-primary-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What People Are Saying
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Johnson, RN',
                role: 'ICU Travel Nurse',
                quote: 'I make $15,000 more per year with Velocity compared to my old agency. The transparency is incredible!',
                image: '👩‍⚕️',
              },
              {
                name: 'Dr. Michael Chen',
                role: 'Chief of Staff, Stanford Medical',
                quote: 'We filled critical ICU positions in 3 hours. This platform is revolutionary for healthcare staffing.',
                image: '👨‍⚕️',
              },
              {
                name: 'Emily Rodriguez, RN',
                role: 'Cardiovascular Specialist',
                quote: 'The AI found me my dream contract in California. I got placed the same day I applied!',
                image: '👩‍⚕️',
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-effect rounded-3xl p-8 hover:bg-white/10 transition-all"
                whileHover={{ y: -5 }}
              >
                <div className="text-6xl mb-4">{testimonial.image}</div>
                <p className="text-gray-300 mb-6 italic">&quot;{testimonial.quote}&quot;</p>
                <div>
                  <div className="text-white font-bold">{testimonial.name}</div>
                  <div className="text-gray-400 text-sm">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 left-10 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
              y: [0, 30, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
            animate={{
              scale: [1, 1.3, 1],
              x: [0, -30, 0],
              y: [0, 50, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-2 px-4 py-2 glass-effect rounded-full mb-8">
              <Sparkles className="w-5 h-5 text-yellow-400" fill="currentColor" />
              <span className="text-sm font-semibold text-gray-200">
                Ready to Transform Your Career?
              </span>
            </div>

            <h2 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Start Earning More
              <br />
              <span className="text-gradient bg-gradient-to-r from-accent-400 to-primary-400">
                Today
              </span>
            </h2>

            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              Join thousands of nurses who&apos;ve discovered a better way.
              Fair pay, instant placements, and complete transparency.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.a
                href="/jobs"
                className="px-10 py-5 bg-gradient-to-r from-accent-500 to-primary-500 rounded-full text-white font-bold text-xl hover:shadow-2xl hover:shadow-accent-500/50 transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Browse Jobs
              </motion.a>
              <motion.button
                className="px-10 py-5 glass-effect rounded-full text-white font-bold text-xl hover:bg-white/20 transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Book a Demo
              </motion.button>
            </div>

            <p className="text-gray-500 text-sm mt-8">
              No credit card required &bull; Setup in 5 minutes &bull; Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="w-6 h-6 text-accent-400" fill="currentColor" />
                <span className="text-xl font-bold text-gradient bg-gradient-to-r from-accent-400 to-primary-400">
                  Velocity
                </span>
              </div>
              <p className="text-gray-400 text-sm">
                Next-generation AI-powered travel nurse staffing platform.
              </p>
            </div>

            {[
              {
                title: 'For Nurses',
                links: [
                  { label: 'Find Jobs', href: '/jobs' },
                  { label: 'My Compliance', href: '/my-compliance' },
                  { label: 'How It Works', href: '#' },
                  { label: 'Resources', href: '#' },
                ],
              },
              {
                title: 'For Hospitals',
                links: [
                  { label: 'Post Jobs', href: '#' },
                  { label: 'Applications', href: '/applications' },
                  { label: 'Compliance', href: '/compliance' },
                  { label: 'Case Studies', href: '#' },
                ],
              },
              {
                title: 'Company',
                links: [
                  { label: 'About', href: '#' },
                  { label: 'Blog', href: '#' },
                  { label: 'Careers', href: '#' },
                  { label: 'Contact', href: '#' },
                ],
              },
            ].map((column, index) => (
              <div key={index}>
                <h4 className="text-white font-semibold mb-4">{column.title}</h4>
                <ul className="space-y-2">
                  {column.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <a href={link.href} className="text-gray-400 hover:text-white transition-colors text-sm">
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
            <p>&copy; 2024 Velocity. All rights reserved. Built with AI for the future of healthcare.</p>
          </div>
        </div>
      </footer>

      <AIChatbot />
    </main>
  )
}
